import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ComposedChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine, Rectangle } from 'recharts';
import { Calculator, RotateCcw, FileDown, TrendingUp, Rocket, Info, Target, PiggyBank, Wallet, Gauge, Settings2 } from 'lucide-react';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import PdfReportAmzAds from './PdfReportAmzAds';
import PdfReportAmzAdsMinimal from './PdfReportAmzAdsMinimal';

// Rate-card FBA par marketplace (barème 2025-02-01)
const FBA_RATES = {
  FR: {
    effective_from: '2025-02-01',
    tiers: {
      enveloppe: [
        { max_dim_cm: [33, 23, 2.5] as [number, number, number], max_weight_g: 100, fee_eur: 2.20 },
        { max_dim_cm: [33, 23, 2.5] as [number, number, number], max_weight_g: 250, fee_eur: 2.60 }
      ],
      colis: [
        { max_dim_cm: [45, 34, 26] as [number, number, number], max_weight_g: 400, fee_eur: 3.20 },
        { max_dim_cm: [45, 34, 26] as [number, number, number], max_weight_g: 950, fee_eur: 3.95 },
        { max_dim_cm: [60, 60, 60] as [number, number, number], max_weight_g: 5000, fee_eur: 5.90 }
      ],
      oversize: [
        { max_dim_cm: [120, 60, 60] as [number, number, number], max_weight_g: 12000, fee_eur: 9.40 }
      ]
    }
  }
};

type Rate = {
  max_dim_cm: [number, number, number];
  max_weight_g: number;
  fee_eur: number;
};

type RateCard = {
  effective_from: string;
  tiers: {
    enveloppe: Rate[];
    colis: Rate[];
    oversize: Rate[];
  };
};

// Tri des dimensions (plus grande en premier)
function sortDims(a: number, b: number, c: number) {
  const arr = [a, b, c].sort((x, y) => y - x);
  return { L: arr[0], W: arr[1], H: arr[2] };
}

// Calcul du FBA selon le barème
function findFbaFee(
  card: RateCard,
  pkg_type: 'enveloppe' | 'colis',
  length: number,
  width: number,
  height: number,
  weight_g: number
): number {
  const { L, W, H } = sortDims(length, width, height);
  const table = card.tiers[pkg_type] ?? card.tiers.colis;

  for (const r of table) {
    const fitsDims = L <= r.max_dim_cm[0] && W <= r.max_dim_cm[1] && H <= r.max_dim_cm[2];
    const fitsWeight = weight_g <= r.max_weight_g;
    if (fitsDims && fitsWeight) return r.fee_eur;
  }

  // Fallback: oversize
  if (card.tiers.oversize?.length) {
    for (const r of card.tiers.oversize) {
      if (weight_g <= r.max_weight_g) return r.fee_eur;
    }
  }

  return 0;
}

// Taux de commission Amazon
function referralRate(price: number): number {
  return price <= 10 ? 0.08 : 0.15;
}

// Constantes par défaut (éditables)
const DEFAULT_SHARE_ADS = {
  launch: 0.70,
  growth: 0.55,
  optimize: 0.40
};

const DEFAULT_TACOS_CAP = {
  launch: 0.15,
  growth: 0.10,
  optimize: 0.08
};

const DEFAULT_CPC_EUR = {
  launch: 0.75,
  growth: 0.65,
  optimize: 0.60
};

const DEFAULT_CVR = {
  launch: 0.08,
  growth: 0.09,
  optimize: 0.10
};

const DEFAULT_MARGIN_BUFFER = 0.05;

// Marges cibles après pub par phase
const MARGIN_TARGET = {
  launch: 0.05,
  growth: 0.12,
  optimize: 0.20
};

interface FormData {
  brandName: string;
  productName: string;
  cogs: string;
  price_launch: string;
  price_target: string;
  units_goal: string;
  market: string;
  length: string;
  width: string;
  height: string;
  weight_g: string;
  pkg_type: 'enveloppe' | 'colis';
  fba_override: string;
}

interface AdvancedParams {
  shareAds: { launch: number; growth: number; optimize: number };
  tacosCap: { launch: number; growth: number; optimize: number };
  cpcEur: { launch: number; growth: number; optimize: number };
  cvr: { launch: number; growth: number; optimize: number };
  marginBuffer: number;
}

interface Economics {
  price: number;
  commission: number;
  fba: number;
  gross_pct: number;
  net_no_ads_eur: number;
  net_no_ads_pct: number;
  acos: { launch: number; growth: number; optimize: number };
  roas: { launch: number | null; growth: number | null; optimize: number | null };
  spend: { launch: number; growth: number; optimize: number };
}

export default function SimulateurAmzAds() {
  const [formData, setFormData] = useState<FormData>({
    brandName: '',
    productName: '',
    cogs: '',
    price_launch: '',
    price_target: '',
    units_goal: '',
    market: 'FR',
    length: '',
    width: '',
    height: '',
    weight_g: '',
    pkg_type: 'colis',
    fba_override: ''
  });

  const [scenario, setScenario] = useState<'launch' | 'target'>('launch');
  const [showResults, setShowResults] = useState(false);
  const [economics, setEconomics] = useState<Economics | null>(null);
  const [fbaEstimated, setFbaEstimated] = useState<number>(0);
  const [showPdfExport, setShowPdfExport] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedParams, setAdvancedParams] = useState<AdvancedParams>({
    shareAds: DEFAULT_SHARE_ADS,
    tacosCap: DEFAULT_TACOS_CAP,
    cpcEur: DEFAULT_CPC_EUR,
    cvr: DEFAULT_CVR,
    marginBuffer: DEFAULT_MARGIN_BUFFER
  });

  // Calcul du FBA estimé
  useEffect(() => {
    const { market, pkg_type, length, width, height, weight_g } = formData;
    if (!length || !width || !height || !weight_g) {
      setFbaEstimated(0);
      return;
    }

    const card = FBA_RATES[market as keyof typeof FBA_RATES];
    if (!card) {
      setFbaEstimated(0);
      return;
    }

    const fee = findFbaFee(
      card,
      pkg_type,
      parseFloat(length),
      parseFloat(width),
      parseFloat(height),
      parseFloat(weight_g)
    );
    setFbaEstimated(fee);
  }, [formData.market, formData.pkg_type, formData.length, formData.width, formData.height, formData.weight_g]);

  // Calcul des economics
  useEffect(() => {
    const { cogs, price_launch, price_target, units_goal, fba_override } = formData;

    if (!cogs || !price_launch || !price_target || !units_goal) {
      setEconomics(null);
      return;
    }

    const cogsNum = parseFloat(cogs);
    const priceLaunchNum = parseFloat(price_launch);
    const priceTargetNum = parseFloat(price_target);
    const unitsGoalNum = parseFloat(units_goal);
    const fbaOverrideNum = fba_override ? parseFloat(fba_override) : null;

    const price = scenario === 'launch' ? priceLaunchNum : priceTargetNum;
    const commission = price * referralRate(price);
    const fba = fbaOverrideNum ?? fbaEstimated;

    const gross_pct = (price - cogsNum) / price;
    const net_no_ads_eur = price - cogsNum - commission - fba;
    const net_no_ads_pct = net_no_ads_eur / price;

    const acos = {
      launch: Math.max(0, net_no_ads_pct - MARGIN_TARGET.launch),
      growth: Math.max(0, net_no_ads_pct - MARGIN_TARGET.growth),
      optimize: Math.max(0, net_no_ads_pct - MARGIN_TARGET.optimize)
    };

    const roas = {
      launch: acos.launch > 0 ? 1 / acos.launch : null,
      growth: acos.growth > 0 ? 1 / acos.growth : null,
      optimize: acos.optimize > 0 ? 1 / acos.optimize : null
    };

    const spend = {
      launch: unitsGoalNum * price * DEFAULT_SHARE_ADS.launch * acos.launch,
      growth: unitsGoalNum * price * DEFAULT_SHARE_ADS.growth * acos.growth,
      optimize: unitsGoalNum * price * DEFAULT_SHARE_ADS.optimize * acos.optimize
    };

    setEconomics({ price, commission, fba, gross_pct, net_no_ads_eur, net_no_ads_pct, acos, roas, spend });
  }, [formData, scenario, fbaEstimated]);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = () => {
    const { brandName, productName, cogs, price_launch, price_target, units_goal } = formData;

    if (!brandName || !productName || !cogs || !price_launch || !price_target || !units_goal) {
      alert('Veuillez remplir tous les champs obligatoires marqués d\'un *');
      return;
    }

    setShowResults(true);
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setFormData({
      brandName: '',
      productName: '',
      cogs: '',
      price_launch: '',
      price_target: '',
      units_goal: '',
      market: 'FR',
      length: '',
      width: '',
      height: '',
      weight_g: '',
      pkg_type: 'colis',
      fba_override: ''
    });
    setScenario('launch');
    setShowResults(false);
    setEconomics(null);
    setShowPdfExport(false);
  };

  const preparePdfData = () => {
    if (!economics) return null;

    const cogsNum = parseFloat(formData.cogs);
    const priceLaunchNum = parseFloat(formData.price_launch);
    const priceTargetNum = parseFloat(formData.price_target);
    const unitsGoalNum = parseFloat(formData.units_goal);
    const fbaOverrideNum = formData.fba_override ? parseFloat(formData.fba_override) : null;
    const fba = fbaOverrideNum ?? fbaEstimated;

    // Scenario Launch
    const commissionLaunch = priceLaunchNum * referralRate(priceLaunchNum);
    const grossPctLaunch = (priceLaunchNum - cogsNum) / priceLaunchNum;
    const netNoAdsEurLaunch = priceLaunchNum - cogsNum - commissionLaunch - fba;
    const netNoAdsPctLaunch = netNoAdsEurLaunch / priceLaunchNum;

    const acosLaunch = {
      launch: Math.max(0, netNoAdsPctLaunch - MARGIN_TARGET.launch),
      growth: Math.max(0, netNoAdsPctLaunch - MARGIN_TARGET.growth),
      optimize: Math.max(0, netNoAdsPctLaunch - MARGIN_TARGET.optimize)
    };

    const roasLaunch = {
      launch: acosLaunch.launch > 0 ? 1 / acosLaunch.launch : null,
      growth: acosLaunch.growth > 0 ? 1 / acosLaunch.growth : null,
      optimize: acosLaunch.optimize > 0 ? 1 / acosLaunch.optimize : null
    };

    const spendLaunch = {
      launch: unitsGoalNum * priceLaunchNum * advancedParams.shareAds.launch * acosLaunch.launch,
      growth: unitsGoalNum * priceLaunchNum * advancedParams.shareAds.growth * acosLaunch.growth,
      optimize: unitsGoalNum * priceLaunchNum * advancedParams.shareAds.optimize * acosLaunch.optimize
    };

    // Scenario Target
    const commissionTarget = priceTargetNum * referralRate(priceTargetNum);
    const grossPctTarget = (priceTargetNum - cogsNum) / priceTargetNum;
    const netNoAdsEurTarget = priceTargetNum - cogsNum - commissionTarget - fba;
    const netNoAdsPctTarget = netNoAdsEurTarget / priceTargetNum;

    const acosTarget = {
      launch: Math.max(0, netNoAdsPctTarget - MARGIN_TARGET.launch),
      growth: Math.max(0, netNoAdsPctTarget - MARGIN_TARGET.growth),
      optimize: Math.max(0, netNoAdsPctTarget - MARGIN_TARGET.optimize)
    };

    const roasTarget = {
      launch: acosTarget.launch > 0 ? 1 / acosTarget.launch : null,
      growth: acosTarget.growth > 0 ? 1 / acosTarget.growth : null,
      optimize: acosTarget.optimize > 0 ? 1 / acosTarget.optimize : null
    };

    const spendTarget = {
      launch: unitsGoalNum * priceTargetNum * advancedParams.shareAds.launch * acosTarget.launch,
      growth: unitsGoalNum * priceTargetNum * advancedParams.shareAds.growth * acosTarget.growth,
      optimize: unitsGoalNum * priceTargetNum * advancedParams.shareAds.optimize * acosTarget.optimize
    };

    return {
      scenarioLaunch: {
        label: "Lancement" as const,
        price: priceLaunchNum,
        cogs: cogsNum,
        unitsGoal: unitsGoalNum,
        grossMarginPct: grossPctLaunch,
        netNoAdsPct: netNoAdsPctLaunch,
        breakEvenPct: netNoAdsPctLaunch,
        phases: {
          launch: {
            budgetEuro: spendLaunch.launch,
            acosPct: acosLaunch.launch,
            roasX: roasLaunch.launch,
            checklist: [
              "SP Auto (4 matchs), SP Exact/Broad (KW cœur)",
              "SB Marque, SD Product Targeting T2",
              "10–20 KW prioritaires, 20–40 ASIN cibles",
              "Négatifs hebdo, +20–40% Top of Search si CR>médiane",
              "A+ live, Brand Store minimal, hooks USP en SB"
            ]
          },
          growth: {
            budgetEuro: spendLaunch.growth,
            acosPct: acosLaunch.growth,
            roasX: roasLaunch.growth,
            checklist: [
              "SP Exact winners, SB Video, SD remarketing 7–30j",
              "Harvesting : expansion L2/L3, requêtes → Exact",
              "Rules by placement, dayparting si utile",
              "Baisse ACOS, part organique ↑"
            ]
          },
          optimize: {
            budgetEuro: spendLaunch.optimize,
            acosPct: acosLaunch.optimize,
            roasX: roasLaunch.optimize,
            checklist: [
              "Conso best performers : regroupement",
              "SB Video marque, SD long remarketing",
              "Negatives systématiques, PBR mensuel",
              "ACOS bas, ROAS ↑, TACoS ↓"
            ]
          }
        },
        waterfall: {
          phase: "launch" as const,
          price: priceLaunchNum,
          cogs: cogsNum,
          commission: commissionLaunch,
          fba,
          adCostPerUnit: priceLaunchNum * acosLaunch.launch * advancedParams.shareAds.launch
        },
        acosSeries: {
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          acosTargetPct: [
            acosLaunch.launch, acosLaunch.launch,
            acosLaunch.growth, acosLaunch.growth, acosLaunch.growth, acosLaunch.growth,
            acosLaunch.optimize, acosLaunch.optimize, acosLaunch.optimize, acosLaunch.optimize, acosLaunch.optimize, acosLaunch.optimize
          ],
          roasTargetX: [
            roasLaunch.launch ?? 0, roasLaunch.launch ?? 0,
            roasLaunch.growth ?? 0, roasLaunch.growth ?? 0, roasLaunch.growth ?? 0, roasLaunch.growth ?? 0,
            roasLaunch.optimize ?? 0, roasLaunch.optimize ?? 0, roasLaunch.optimize ?? 0, roasLaunch.optimize ?? 0, roasLaunch.optimize ?? 0, roasLaunch.optimize ?? 0
          ],
          breakEvenPct: netNoAdsPctLaunch
        }
      },
      scenarioTarget: {
        label: "Cible" as const,
        price: priceTargetNum,
        cogs: cogsNum,
        unitsGoal: unitsGoalNum,
        grossMarginPct: grossPctTarget,
        netNoAdsPct: netNoAdsPctTarget,
        breakEvenPct: netNoAdsPctTarget,
        phases: {
          launch: {
            budgetEuro: spendTarget.launch,
            acosPct: acosTarget.launch,
            roasX: roasTarget.launch,
            checklist: [
              "SP Auto (4 matchs), SP Exact/Broad (KW cœur)",
              "SB Marque, SD Product Targeting T2",
              "10–20 KW prioritaires, 20–40 ASIN cibles",
              "Négatifs hebdo, +20–40% Top of Search si CR>médiane",
              "A+ live, Brand Store minimal, hooks USP en SB"
            ]
          },
          growth: {
            budgetEuro: spendTarget.growth,
            acosPct: acosTarget.growth,
            roasX: roasTarget.growth,
            checklist: [
              "SP Exact winners, SB Video, SD remarketing 7–30j",
              "Harvesting : expansion L2/L3, requêtes → Exact",
              "Rules by placement, dayparting si utile",
              "Baisse ACOS, part organique ↑"
            ]
          },
          optimize: {
            budgetEuro: spendTarget.optimize,
            acosPct: acosTarget.optimize,
            roasX: roasTarget.optimize,
            checklist: [
              "Conso best performers : regroupement",
              "SB Video marque, SD long remarketing",
              "Negatives systématiques, PBR mensuel",
              "ACOS bas, ROAS ↑, TACoS ↓"
            ]
          }
        },
        waterfall: {
          phase: "optimize" as const,
          price: priceTargetNum,
          cogs: cogsNum,
          commission: commissionTarget,
          fba,
          adCostPerUnit: priceTargetNum * acosTarget.optimize * advancedParams.shareAds.optimize
        },
        acosSeries: {
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          acosTargetPct: [
            acosTarget.launch, acosTarget.launch,
            acosTarget.growth, acosTarget.growth, acosTarget.growth, acosTarget.growth,
            acosTarget.optimize, acosTarget.optimize, acosTarget.optimize, acosTarget.optimize, acosTarget.optimize, acosTarget.optimize
          ],
          roasTargetX: [
            roasTarget.launch ?? 0, roasTarget.launch ?? 0,
            roasTarget.growth ?? 0, roasTarget.growth ?? 0, roasTarget.growth ?? 0, roasTarget.growth ?? 0,
            roasTarget.optimize ?? 0, roasTarget.optimize ?? 0, roasTarget.optimize ?? 0, roasTarget.optimize ?? 0, roasTarget.optimize ?? 0, roasTarget.optimize ?? 0
          ],
          breakEvenPct: netNoAdsPctTarget
        }
      }
    };
  };

  const [activePhase, setActivePhase] = useState<'launch' | 'growth' | 'optimize'>('launch');

  // Fonction de calcul budget crédible avec garde-fous
  const calculateBudget = (phase: 'launch' | 'growth' | 'optimize', price: number, unitsGoal: number, breakEvenPct: number, acosTarget: number) => {
    const { shareAds, tacosCap, cpcEur, cvr, marginBuffer } = advancedParams;
    
    const unitsAds = unitsGoal * shareAds[phase];
    const acosCeiling = Math.max(0, breakEvenPct - marginBuffer);
    
    const budgetAcos = (unitsGoal * price * shareAds[phase]) * Math.min(acosTarget, acosCeiling);
    const budgetTraffic = unitsAds * cpcEur[phase] / cvr[phase];
    const budgetTacosCap = (unitsGoal * price) * tacosCap[phase];
    
    const budgetFinal = Math.round(Math.min(budgetAcos, budgetTraffic, budgetTacosCap));
    const tacos = budgetFinal / (unitsGoal * price);
    const credibility = budgetFinal === budgetTraffic || budgetFinal === budgetTacosCap ? 'ok' : 
                        tacos > tacosCap[phase] ? 'aggressive' : 'ok';
    
    return { budgetFinal, credibility, tacos, budgetAcos, budgetTraffic, budgetTacosCap };
  };

  // Budget par phase avec garde-fous
  const budgetLaunchData = economics ? calculateBudget('launch', economics.price, parseFloat(formData.units_goal), economics.net_no_ads_pct, economics.acos.launch) : null;
  const budgetGrowthData = economics ? calculateBudget('growth', economics.price, parseFloat(formData.units_goal), economics.net_no_ads_pct, economics.acos.growth) : null;
  const budgetOptimizeData = economics ? calculateBudget('optimize', economics.price, parseFloat(formData.units_goal), economics.net_no_ads_pct, economics.acos.optimize) : null;

  const budgetLaunch = budgetLaunchData?.budgetFinal ?? 0;
  const budgetGrowth = budgetGrowthData?.budgetFinal ?? 0;
  const budgetOptimize = budgetOptimizeData?.budgetFinal ?? 0;

  // Budget de la phase active
  const activeBudgetData = activePhase === 'launch' ? budgetLaunchData : activePhase === 'growth' ? budgetGrowthData : budgetOptimizeData;
  const budgetEuroMonth = activeBudgetData?.budgetFinal ?? 0;
  const budgetEuroDay = budgetEuroMonth / 30;

  // Helper: générer waterfall pour un scénario
  const generateWaterfallData = (price: number, acos: number, shareAds: number) => {
    if (!economics) return [];
    
    const cogs = parseFloat(formData.cogs);
    const commission = price * referralRate(price);
    const fba = economics.fba;
    const netNoAdsEur = price - cogs - commission - fba;
    const adCostPerUnit = price * acos * shareAds;

    let acc = 0;
    const segments: Array<{ label: string; delta: number; color: string }> = [
      { label: 'Prix', delta: price, color: '#6b7142' },
      { label: '− COGS', delta: -cogs, color: '#8b8b8b' },
      { label: '− Commission', delta: -commission, color: '#9b9b9b' },
      { label: '− FBA', delta: -fba, color: '#b0b0b0' },
      { label: 'Marge nette (hors pub)', delta: netNoAdsEur, color: '#7a6bff' },
      { label: '− Coût pub/unité', delta: -adCostPerUnit, color: '#c0c0c0' },
      { label: 'Marge nette (après pub)', delta: netNoAdsEur - adCostPerUnit, color: '#6b7142' }
    ];

    return segments.map((s) => {
      const base = Math.min(acc, acc + s.delta);
      const delta = s.delta;
      acc += s.delta;
      return { label: s.label, base, delta, color: s.color, value: acc };
    });
  };

  // Chart data: Waterfall unitaire (décomposition cumulative)
  const waterfallData = economics
    ? generateWaterfallData(economics.price, economics.acos[activePhase], advancedParams.shareAds[activePhase])
    : [];
  
  // Waterfall data pour PDF (scénario Launch et Target)
  // Calculer l'ACOS pour chaque scénario
  const priceLaunchNum = parseFloat(formData.price_launch);
  const priceTargetNum = parseFloat(formData.price_target);
  const cogsNum = parseFloat(formData.cogs);
  
  const commLaunch = priceLaunchNum * referralRate(priceLaunchNum);
  const netNoAdsLaunch = priceLaunchNum - cogsNum - commLaunch - (economics?.fba ?? 0);
  const netNoAdsPctLaunch = netNoAdsLaunch / priceLaunchNum;
  const acosLaunchPhase = Math.max(0, netNoAdsPctLaunch - MARGIN_TARGET.launch);
  
  const commTarget = priceTargetNum * referralRate(priceTargetNum);
  const netNoAdsTarget = priceTargetNum - cogsNum - commTarget - (economics?.fba ?? 0);
  const netNoAdsPctTarget = netNoAdsTarget / priceTargetNum;
  const acosTargetPhase = Math.max(0, netNoAdsPctTarget - MARGIN_TARGET.optimize);
  
  const waterfallDataLaunchPDF = generateWaterfallData(
    priceLaunchNum,
    acosLaunchPhase,
    advancedParams.shareAds.launch
  );
  
  const waterfallDataTargetPDF = generateWaterfallData(
    priceTargetNum,
    acosTargetPhase,
    advancedParams.shareAds.optimize
  );

  // Chart data: ACOS & ROAS évolution
  const evolutionData = economics
    ? [
        {
          mois: 'Mois 1-2',
          acos_be: economics.net_no_ads_pct * 100,
          acos_cible: economics.acos.launch * 100,
          roas: economics.roas.launch
        },
        {
          mois: 'Mois 3-6',
          acos_be: economics.net_no_ads_pct * 100,
          acos_cible: economics.acos.growth * 100,
          roas: economics.roas.growth
        },
        {
          mois: 'Mois 6+',
          acos_be: economics.net_no_ads_pct * 100,
          acos_cible: economics.acos.optimize * 100,
          roas: economics.roas.optimize
        }
      ]
    : [];

  return (
    <div className="space-y-8">
      <TooltipProvider>
        {/* Formulaire */}
        <Card className="p-8 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Calculator className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h2 className="text-3xl font-bold text-primary mb-2">Simulateur Amazon Ads</h2>
            <p className="text-muted-foreground">
              Calcul automatique avec barème FBA local et commission Amazon
            </p>
          </div>

          {/* Données essentielles */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Données essentielles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="brandName">
                  Nom de la marque <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="brandName"
                  placeholder="Ex: Ma Marque"
                  value={formData.brandName}
                  onChange={(e) => updateField('brandName', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="productName">
                  Nom du produit <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="productName"
                  placeholder="Ex: Mon Produit"
                  value={formData.productName}
                  onChange={(e) => updateField('productName', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="cogs">
                  COGS / Coût de revient (€) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="cogs"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 8.50"
                  value={formData.cogs}
                  onChange={(e) => updateField('cogs', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="price_launch">
                  Prix de lancement TTC (€) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="price_launch"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 19.90"
                  value={formData.price_launch}
                  onChange={(e) => updateField('price_launch', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="price_target">
                  Prix cible TTC (€) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="price_target"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 24.90"
                  value={formData.price_target}
                  onChange={(e) => updateField('price_target', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="units_goal">
                  Objectif de ventes mensuelles (unités) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="units_goal"
                  type="number"
                  placeholder="Ex: 300"
                  value={formData.units_goal}
                  onChange={(e) => updateField('units_goal', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Paramètres logistiques FBA */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Paramètres logistiques (FBA)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="market">Marketplace</Label>
                <Select value={formData.market} onValueChange={(value) => updateField('market', value)}>
                  <SelectTrigger id="market">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FR">France (FR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pkg_type">Type d'envoi</Label>
                <Select
                  value={formData.pkg_type}
                  onValueChange={(value: 'enveloppe' | 'colis') => updateField('pkg_type', value)}
                >
                  <SelectTrigger id="pkg_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enveloppe">Enveloppe</SelectItem>
                    <SelectItem value="colis">Colis</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="length" className="flex items-center gap-2">
                  Longueur (cm)
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Dimensions emballées (prêtes FBA)</p>
                    </TooltipContent>
                  </UITooltip>
                </Label>
                <Input
                  id="length"
                  type="number"
                  step="0.1"
                  placeholder="Ex: 30"
                  value={formData.length}
                  onChange={(e) => updateField('length', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="width">Largeur (cm)</Label>
                <Input
                  id="width"
                  type="number"
                  step="0.1"
                  placeholder="Ex: 20"
                  value={formData.width}
                  onChange={(e) => updateField('width', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="height">Hauteur (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.1"
                  placeholder="Ex: 5"
                  value={formData.height}
                  onChange={(e) => updateField('height', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="weight_g">Poids total (g)</Label>
                <Input
                  id="weight_g"
                  type="number"
                  placeholder="Ex: 250"
                  value={formData.weight_g}
                  onChange={(e) => updateField('weight_g', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="fba_estimated" className="flex items-center gap-2">
                  FBA estimé / unité (€)
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Barème interne {FBA_RATES.FR.effective_from}. Dimensions/Poids emballés (prêtes FBA).</p>
                    </TooltipContent>
                  </UITooltip>
                </Label>
                <div className="flex gap-2 items-center">
                  <Input id="fba_estimated" type="text" value={fbaEstimated.toFixed(2)} disabled />
                  {fbaEstimated === 0 && formData.length && formData.width && formData.height && formData.weight_g && (
                    <span className="text-xs text-amber-600">⚠️ Barème non trouvé</span>
                  )}
                </div>
                {fbaEstimated > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Source: Barème interne (FR)</p>
                )}
                {formData.fba_override && (
                  <p className="text-xs text-muted-foreground mt-1">Source: Override manuel</p>
                )}
              </div>

              <div>
                <Label htmlFor="fba_override" className="flex items-center gap-2">
                  Override FBA (€)
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Valeur connue prioritaire sur l'estimation</p>
                    </TooltipContent>
                  </UITooltip>
                </Label>
                <Input
                  id="fba_override"
                  type="number"
                  step="0.01"
                  placeholder="Optionnel"
                  value={formData.fba_override}
                  onChange={(e) => updateField('fba_override', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Paramètres avancés (collapsible) */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between text-sm"
            >
              <span className="flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Paramètres avancés (CPC, CVR, TACoS cap, etc.)
              </span>
              <span>{showAdvanced ? '−' : '+'}</span>
            </Button>
            
            {showAdvanced && (
              <div className="mt-4 p-4 border border-border rounded-lg bg-muted/30 space-y-4">
                {/* Explication avec exemple */}
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 text-sm leading-6">
                  <div className="font-medium mb-2" style={{color:'#6b7142'}}>À quoi servent ces paramètres ?</div>
                  <p className="text-zinc-300 mb-2">
                    Nous utilisons ces valeurs pour donner un <b>budget crédible</b> et éviter les sur-enchères.
                    Le <b>CPC</b> (coût par clic) et le <b>CVR</b> (taux de conversion) estiment le trafic nécessaire
                    pour atteindre les unités cibles ; le <b>cap TACoS</b> borne la part pub dans le CA total ; le{' '}
                    <b>buffer de marge</b> (ex. 5 pts) garantit que l'ACOS cible reste sous le break-even.
                  </p>
                  <p className="text-zinc-400 text-xs">
                    <i>Exemple :</i> 500 unités/mois, prix 39,99 €, <b>CPC</b> 0,70 €, <b>CVR</b> 8 %, part Ads 55 % →
                    ~3 438 clics nécessaires (275 unités Ads / 0,08). Budget trafic ≈ 3 438 × 0,70 = <b>2 407 €</b>.
                    Le budget final prendra le <b>minimum</b> entre (ACOS, Trafic, Cap TACoS).
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Share Ads */}
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">Part ventes Ads (%)</Label>
                    <div className="space-y-2">
                      {(['launch', 'growth', 'optimize'] as const).map(phase => (
                        <div key={phase} className="flex items-center gap-2">
                          <span className="text-xs w-20">{phase === 'launch' ? 'Lancement' : phase === 'growth' ? 'Croissance' : 'Optimisation'}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={(advancedParams.shareAds[phase] * 100).toFixed(0)}
                            onChange={(e) => setAdvancedParams({
                              ...advancedParams,
                              shareAds: { ...advancedParams.shareAds, [phase]: parseFloat(e.target.value) / 100 }
                            })}
                            className="h-8 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* TACoS Cap */}
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">Cap TACoS (%)</Label>
                    <div className="space-y-2">
                      {(['launch', 'growth', 'optimize'] as const).map(phase => (
                        <div key={phase} className="flex items-center gap-2">
                          <span className="text-xs w-20">{phase === 'launch' ? 'Lancement' : phase === 'growth' ? 'Croissance' : 'Optimisation'}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={(advancedParams.tacosCap[phase] * 100).toFixed(0)}
                            onChange={(e) => setAdvancedParams({
                              ...advancedParams,
                              tacosCap: { ...advancedParams.tacosCap, [phase]: parseFloat(e.target.value) / 100 }
                            })}
                            className="h-8 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CPC */}
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">CPC moyen (€)</Label>
                    <div className="space-y-2">
                      {(['launch', 'growth', 'optimize'] as const).map(phase => (
                        <div key={phase} className="flex items-center gap-2">
                          <span className="text-xs w-20">{phase === 'launch' ? 'Lancement' : phase === 'growth' ? 'Croissance' : 'Optimisation'}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={advancedParams.cpcEur[phase].toFixed(2)}
                            onChange={(e) => setAdvancedParams({
                              ...advancedParams,
                              cpcEur: { ...advancedParams.cpcEur, [phase]: parseFloat(e.target.value) }
                            })}
                            className="h-8 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* CVR */}
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">Taux de conversion (%)</Label>
                    <div className="space-y-2">
                      {(['launch', 'growth', 'optimize'] as const).map(phase => (
                        <div key={phase} className="flex items-center gap-2">
                          <span className="text-xs w-20">{phase === 'launch' ? 'Lancement' : phase === 'growth' ? 'Croissance' : 'Optimisation'}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={(advancedParams.cvr[phase] * 100).toFixed(0)}
                            onChange={(e) => setAdvancedParams({
                              ...advancedParams,
                              cvr: { ...advancedParams.cvr, [phase]: parseFloat(e.target.value) / 100 }
                            })}
                            className="h-8 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Margin Buffer */}
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">Marge de sécurité (% points)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(advancedParams.marginBuffer * 100).toFixed(0)}
                      onChange={(e) => setAdvancedParams({
                        ...advancedParams,
                        marginBuffer: parseFloat(e.target.value) / 100
                      })}
                      className="h-8 text-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-1">ACOS cible ≤ break-even − {(advancedParams.marginBuffer * 100).toFixed(0)} pts</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button onClick={handleGenerate} className="flex-1" size="lg">
              <Calculator className="w-5 h-5 mr-2" />
              Générer la stratégie
            </Button>
            <Button onClick={handleReset} variant="outline" size="lg">
              <RotateCcw className="w-5 h-5 mr-2" />
              Réinitialiser
            </Button>
          </div>
        </Card>

        {/* Résultats */}
        {showResults && economics && (
          <div id="results-section" className="space-y-8">
            {/* Scénario actif — Séparé en 2 groupes */}
            <div className="mb-6 max-w-4xl mx-auto flex flex-col md:flex-row gap-3 items-start md:items-center justify-between rounded-2xl border-2 border-primary/30 p-6 bg-card">
              {/* Groupe A : Scénario de prix */}
              <div>
                <div className="text-sm text-muted-foreground mb-2">Scénario de prix</div>
                <div className="inline-flex gap-2">
                  <Button 
                    variant={scenario === 'launch' ? 'default' : 'ghost'} 
                    onClick={() => setScenario('launch')}
                    size="sm"
                  >
                    Prix Lancement
                  </Button>
                  <Button 
                    variant={scenario === 'target' ? 'default' : 'ghost'} 
                    onClick={() => setScenario('target')}
                    size="sm"
                  >
                    Prix Cible
                  </Button>
                </div>
                <div className="mt-1 text-xs text-muted-foreground max-w-[280px]">
                  Le scénario de prix détermine le PVP utilisé dans tous les calculs.
                </div>
                <div className="mt-2 text-sm font-semibold text-primary">
                  {scenario === 'launch' 
                    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(parseFloat(formData.price_launch))
                    : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(parseFloat(formData.price_target))
                  }
                </div>
              </div>

              {/* Groupe B : Phase de campagne */}
              <div>
                <div className="text-sm text-muted-foreground mb-2">Phase de campagne</div>
                <div className="inline-flex gap-2">
                  <Button
                    size="sm"
                    variant={activePhase === 'launch' ? 'default' : 'ghost'}
                    onClick={() => setActivePhase('launch')}
                  >
                    Lancement
                  </Button>
                  <Button
                    size="sm"
                    variant={activePhase === 'growth' ? 'default' : 'ghost'}
                    onClick={() => setActivePhase('growth')}
                  >
                    Croissance
                  </Button>
                  <Button
                    size="sm"
                    variant={activePhase === 'optimize' ? 'default' : 'ghost'}
                    onClick={() => setActivePhase('optimize')}
                  >
                    Optimisation
                  </Button>
                </div>
                <div className="mt-1 text-xs text-muted-foreground max-w-[320px]">
                  La phase ajuste ACOS cible, mix Ads/Organiques et budget recommandé.
                </div>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6 text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-3xl font-bold text-primary">{(economics.gross_pct * 100).toFixed(1)}%</div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  Marge brute
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>(Prix − COGS) / Prix. N'inclut ni commission ni FBA.</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-2">
                  (Prix − COGS) / Prix
                </div>
              </Card>

              <Card className="p-6 text-center">
                <PiggyBank className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-3xl font-bold text-primary">{(economics.net_no_ads_pct * 100).toFixed(1)}%</div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  Marge nette (hors pub)
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>(Prix − COGS − Commission − FBA) / Prix</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-2">
                  (Prix − COGS − Commission − FBA) / Prix
                </div>
              </Card>

              <Card className="p-6 text-center">
                <Target className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-3xl font-bold text-primary">{(economics.net_no_ads_pct * 100).toFixed(1)}%</div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  ACOS break-even
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold mb-1">Seuil de rentabilité publicitaire</p>
                      <p className="text-xs">Le pourcentage maximum de dépenses pub par rapport aux ventes pour rester rentable. Au-delà de ce seuil, la marge devient négative.</p>
                      <p className="text-xs mt-1 text-primary">✓ Viser ACOS réel ≤ ACOS break-even</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-2">
                  Seuil de rentabilité pub
                </div>
              </Card>

              <Card className="p-6 text-center">
                <Wallet className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-2xl font-bold text-primary metric-value" style={{ fontSize: 'clamp(13px, 1.5vw, 18px)', lineHeight: 1 }}>
                  €{Math.round(budgetEuroMonth)}
                </div>
                <div className="text-xs text-muted-foreground/60 mt-1">
                  par mois (phase active)
                </div>
                <div className="text-base font-semibold text-primary mt-2" style={{ fontSize: 'clamp(12px, 1.3vw, 16px)' }}>
                  €{Math.round(budgetEuroDay)}/jour
                </div>
                <div className="text-xs text-muted-foreground/60 mt-2">
                  Plage phases : €{Math.round(Math.min(budgetLaunch, budgetGrowth, budgetOptimize))}–€{Math.round(Math.max(budgetLaunch, budgetGrowth, budgetOptimize))}
                </div>
                {/* Badge Crédibilité */}
                {activeBudgetData && (
                  <div className="mt-3 flex items-center justify-center gap-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      activeBudgetData.credibility === 'ok' 
                        ? 'bg-green-500/10 text-green-700 dark:text-green-400' 
                        : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    }`}>
                      {activeBudgetData.credibility === 'ok' ? '✓ Crédible' : '⚠︎ Agressif'}
                    </span>
                    <UITooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs mb-1">Budget borné par 3 garde-fous :</p>
                        <ul className="text-xs space-y-0.5">
                          <li>• CPC/CVR : €{Math.round(activeBudgetData.budgetTraffic)}</li>
                          <li>• Cap TACoS : €{Math.round(activeBudgetData.budgetTacosCap)}</li>
                          <li>• Marge (BE−{(advancedParams.marginBuffer*100).toFixed(0)}pts) : €{Math.round(activeBudgetData.budgetAcos)}</li>
                        </ul>
                        <p className="text-xs mt-1 text-primary">TACoS estimé : {(activeBudgetData.tacos * 100).toFixed(1)}%</p>
                      </TooltipContent>
                    </UITooltip>
                  </div>
                )}
                <div className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
                  Budget pub mensuel
                </div>
              </Card>

              <Card className="p-6 text-center">
                <Gauge className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-xl font-bold text-primary">
                  {(economics.acos[activePhase] * 100).toFixed(1)}% / {economics.roas[activePhase]?.toFixed(2)}×
                </div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  Cible : ACOS / ROAS
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>ROAS = 1 / ACOS (en décimal)</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-2">
                  ROAS = 1 / ACOS
                </div>
              </Card>

              {/* Nouvelle card : Marge après pub */}
              <Card className="p-6 text-center">
                <PiggyBank className="w-8 h-8 mx-auto mb-2 text-primary opacity-90" />
                <div className="text-3xl font-bold text-primary">
                  {(() => {
                    const marginAfterAds = (economics.net_no_ads_pct - economics.acos[activePhase]) * 100;
                    return `${marginAfterAds.toFixed(1)}%`;
                  })()}
                </div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                  Marge après pub
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Marge nette après prise en compte du budget pub de la phase active.</p>
                      <p className="text-xs mt-1">Formule : Marge nette (hors pub) − ACOS cible</p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-2">
                  Scénario {scenario === 'launch' ? 'Lancement' : 'Cible'} — Phase {activePhase === 'launch' ? 'Lancement' : activePhase === 'growth' ? 'Croissance' : 'Optimisation'}
                </div>
              </Card>
            </div>

            {/* Plans d'actions par phase — réécriture langage client */}
            <Card className="p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-6 text-primary">Plans d'actions par phase</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Lancement (2–3 mois) */}
                <div className="p-6 border-2 rounded-lg bg-card border-primary/30">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-primary/20">
                    <Rocket className="w-8 h-8 text-primary" />
                    <div>
                      <h4 className="text-lg font-bold text-primary">Lancement</h4>
                      <p className="text-xs text-muted-foreground">2–3 mois</p>
                    </div>
                  </div>
                  
                  {/* KPIs */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-border bg-muted/30 p-3 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Budget conseillé</span>
                      <span className="text-sm font-bold text-primary">
                        €{budgetLaunch.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ACOS cible</span>
                      <span className="text-sm font-bold text-primary">{(economics.acos.launch * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ROAS cible</span>
                      <span className="text-sm font-bold text-primary">{economics.roas.launch?.toFixed(2) ?? 'N/A'}×</span>
                    </div>
                  </div>
                  
                  {/* 5 sous-sections */}
                  <div className="space-y-3 text-xs">
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Objectif</h5>
                      <p className="text-muted-foreground">Obtenir rapidement de la traction et des données fiables tout en posant les bases organiques.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Stratégie média</h5>
                      <p className="text-muted-foreground">SP Automatique (4 correspondances) pour exploration ; SP Manuel (Exact & Broad) sur 10–20 mots-clés cœur ; SB Marque pour capter la demande existante ; SD ciblage produits concurrents (niveau 2).</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Ciblage & volumétrie</h5>
                      <p className="text-muted-foreground">10–20 KW prioritaires ; 20–40 ASIN ciblés ; budget concentré sur 4–6 ad groups tests ; placements Top-of-Search boostés si CR ≥ médiane.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Créa & retail readiness</h5>
                      <p className="text-muted-foreground">A+ actif (bénéfices/matières/FAQ), Brand Store minimal (catégories + USP), premières hooks vidéo en SB.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Gouvernance & rituels</h5>
                      <p className="text-muted-foreground">Négatifs hebdomadaires, revue des requêtes 2×/semaine, règles par emplacement, point de suivi hebdo (ACOS ≤ cible, SoV sur 10 KW stratégiques).</p>
                    </div>
                  </div>
                </div>
                
                {/* Croissance (3–6 mois) */}
                <div className="p-6 border-2 rounded-lg bg-card border-primary/30">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-primary/20">
                    <TrendingUp className="w-8 h-8 text-primary" />
                    <div>
                      <h4 className="text-lg font-bold text-primary">Croissance</h4>
                      <p className="text-xs text-muted-foreground">3–6 mois</p>
                    </div>
                  </div>
                  
                  {/* KPIs */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-border bg-muted/30 p-3 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Budget conseillé</span>
                      <span className="text-sm font-bold text-primary">
                        €{budgetGrowth.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ACOS cible</span>
                      <span className="text-sm font-bold text-primary">{(economics.acos.growth * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ROAS cible</span>
                      <span className="text-sm font-bold text-primary">{economics.roas.growth?.toFixed(2) ?? 'N/A'}×</span>
                    </div>
                  </div>
                  
                  {/* 5 sous-sections */}
                  <div className="space-y-3 text-xs">
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Objectif</h5>
                      <p className="text-muted-foreground">Accroître le volume en maîtrisant le coût d'acquisition.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Stratégie média</h5>
                      <p className="text-muted-foreground">SP Exact sur "winners" ; SB Video pour élargir l'audience ; SD remarketing 7–30 jours.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Ciblage & volumétrie</h5>
                      <p className="text-muted-foreground">Harvesting continu : requêtes convertissantes → Exact ; expansion L2/L3 ; pilotage par ad groups thématiques.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Créa & retail readiness</h5>
                      <p className="text-muted-foreground">Itérations visuelles (claims, bénéfices), a/b tests images en SP, vitrines Store enrichies.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Gouvernance & rituels</h5>
                      <p className="text-muted-foreground">Dayparting si utile, réallocation budgétaire bimensuelle, surveillance TACoS et part organique en hausse.</p>
                    </div>
                  </div>
                </div>

                {/* Optimisation (permanent) */}
                <div className="p-6 border-2 rounded-lg bg-card border-primary/30">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-primary/20">
                    <Settings2 className="w-8 h-8 text-primary" />
                    <div>
                      <h4 className="text-lg font-bold text-primary">Optimisation</h4>
                      <p className="text-xs text-muted-foreground">Permanent</p>
                    </div>
                  </div>
                  
                  {/* KPIs */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-border bg-muted/30 p-3 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Budget conseillé</span>
                      <span className="text-sm font-bold text-primary">
                        €{budgetOptimize.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ACOS cible</span>
                      <span className="text-sm font-bold text-primary">{(economics.acos.optimize * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ROAS cible</span>
                      <span className="text-sm font-bold text-primary">{economics.roas.optimize?.toFixed(2) ?? 'N/A'}×</span>
                    </div>
                  </div>
                  
                  {/* 5 sous-sections */}
                  <div className="space-y-3 text-xs">
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Objectif</h5>
                      <p className="text-muted-foreground">Maximiser la rentabilité et stabiliser le mix organique/Ads.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Stratégie média</h5>
                      <p className="text-muted-foreground">Consolidation des best performers ; SB Video Marque ; SD remarketing long.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Ciblage & volumétrie</h5>
                      <p className="text-muted-foreground">Rationalisation (moins d'ad groups, plus d'assertivité), plafonds sur les placements chers.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Créa & retail readiness</h5>
                      <p className="text-muted-foreground">Storytelling marque, pages Store saisonnières, UGC/avant-après si disponible.</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary mb-1">Gouvernance & rituels</h5>
                      <p className="text-muted-foreground">Négatifs systématiques, PBR (reallocation) mensuel, objectifs : ROAS ↑, TACoS ↓, marge après pub ↑.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Charts */}
            <div className="max-w-4xl mx-auto grid grid-cols-1 gap-8">
              {/* Chart A: Waterfall */}
              <Card className="p-8">
                <h3 className="text-xl font-bold mb-6 text-primary">Décomposition unitaire (Waterfall)</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={waterfallData} margin={{ top: 8, right: 16, bottom: 32, left: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                     <XAxis 
                      dataKey="label" 
                      interval={0} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} 
                      tickMargin={12}
                      angle={-15}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      tickFormatter={(v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v)} 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} 
                    />
                    <Tooltip 
                      content={({ active, payload }: any) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload;
                        return (
                          <div 
                            className="rounded-xl px-3 py-2 shadow-lg"
                            style={{ 
                              background: 'rgba(0,0,0,0.9)', 
                              border: '1px solid #6b7142', 
                              color: '#fff' 
                            }}
                          >
                            <div className="font-semibold mb-1" style={{ color: '#6b7142' }}>
                              {p.label}
                            </div>
                            <div className="text-sm">
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p.delta)}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine y={0} stroke="hsl(var(--border))" />
                    <Bar dataKey="base" stackId="wf" fill="transparent" />
                    <Bar 
                      dataKey="delta" 
                      stackId="wf" 
                      radius={[4, 4, 0, 0]} 
                      label={{ 
                        position: 'top', 
                        formatter: (v: any) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v),
                        fontSize: 10 
                      }}
                      shape={(props: any) => <Rectangle {...props} fill={props.payload.color} />}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>

              {/* Chart B: ACOS & ROAS */}
              <Card className="p-8">
                <h3 className="text-xl font-bold mb-6 text-primary">Évolution ACOS & ROAS</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={evolutionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="mois" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis yAxisId="left" label={{ value: 'ACOS %', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' } }} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{ value: 'ROAS ×', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' } }}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))', 
                        borderRadius: '0.5rem',
                        color: 'hsl(var(--foreground))',
                        fontSize: 12 
                      }} 
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="left" type="monotone" dataKey="acos_be" stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" strokeWidth={2} name="ACOS Break-even" dot={{ r: 4 }} />
                    <Line yAxisId="left" type="monotone" dataKey="acos_cible" stroke="#6b7142" strokeWidth={2} name="ACOS Cible" dot={{ r: 4, fill: '#6b7142' }} />
                    <Line yAxisId="right" type="monotone" dataKey="roas" stroke="#7a6bff" strokeWidth={2} name="ROAS Cible" dot={{ r: 3, fill: '#7a6bff' }} />
                  </LineChart>
                </ResponsiveContainer>
                
                {/* Légende + caption */}
                <div className="mt-4 text-xs text-muted-foreground space-y-2">
                  <div className="flex flex-wrap gap-4">
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-6 h-0.5 align-middle bg-[#6b7142]" />
                      ACOS cible
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-6 h-0.5 align-middle bg-[#7a6bff]" />
                      ROAS (×)
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-6 h-0.5 align-middle bg-[#bbb]" style={{ borderTop: '2px dashed #bbb', height: 0 }} />
                      Break-even (pointillé)
                    </span>
                  </div>
                   <div className="text-xs leading-relaxed">
                     <strong>Lecture :</strong> Si la courbe <strong className="text-primary">ACOS cible</strong> dépasse la ligne{' '}
                     <strong className="text-primary">break-even</strong>, la <strong className="text-primary">marge après pub</strong>{' '}
                     devient négative. Rappel : <strong className="text-primary">ROAS = 1 / ACOS</strong> (en décimal).
                   </div>
                </div>
              </Card>

              {/* Chart C: Sensibilité ACOS → Marge */}
              <Card className="p-8">
                <h3 className="text-xl font-bold mb-6 text-primary">Sensibilité ACOS → Marge après pub</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart 
                    data={(() => {
                      const breakEvenPct = economics.net_no_ads_pct;
                      const acosTarget = economics.acos[scenario === 'launch' ? 'launch' : 'optimize'];
                      const maxAcos = breakEvenPct + 0.1;
                      return Array.from({ length: 11 }, (_, i) => {
                        const acos = (i * maxAcos) / 10;
                        return {
                          x: `${(acos * 100).toFixed(0)}%`,
                          marginAfterAds: (breakEvenPct - acos) * 100,
                          acosValue: acos
                        };
                      });
                    })()}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="x" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis 
                      unit=" %" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      label={{ value: 'Marge après pub (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))', 
                        borderRadius: '0.5rem',
                        fontSize: 12 
                      }} 
                    />
                    <ReferenceLine 
                      x={`${(economics.acos[scenario === 'launch' ? 'launch' : 'optimize'] * 100).toFixed(0)}%`} 
                      stroke="#6b7142" 
                      strokeWidth={2}
                      label={{ value: 'ACOS cible', position: 'top', fill: '#6b7142', fontSize: 11 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="marginAfterAds" 
                      stroke="#6b7142" 
                      strokeWidth={2} 
                      dot={{ r: 4, fill: '#6b7142' }} 
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 text-xs text-muted-foreground">
                  Impact d'un ACOS donné sur votre marge nette finale. Plus l'ACOS est bas, plus la marge après pub est élevée.
                </div>
              </Card>

              {/* Chart D: Mix Ads vs Organiques */}
              <Card className="p-8">
                <h3 className="text-xl font-bold mb-6 text-primary">Répartition Ventes : Ads vs Organiques</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart 
                    data={[
                      { phase: 'Lancement', Ads: 85, Org: 15 },
                      { phase: 'Croissance', Ads: 65, Org: 35 },
                      { phase: 'Optimisation', Ads: 45, Org: 55 }
                    ]}
                    margin={{ top: 8, right: 16, bottom: 16, left: 12 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="phase" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis 
                      unit=" %" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      label={{ value: 'Part des ventes (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))', 
                        borderRadius: '0.5rem',
                        fontSize: 12 
                      }} 
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Ads" stackId="a" fill="#6b7142" name="Ventes Ads" />
                    <Bar dataKey="Org" stackId="a" fill="#7a6bff" name="Ventes Organiques" />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="mt-2 text-xs text-muted-foreground">
                  Évolution attendue du mix de ventes. Objectif : augmenter progressivement la part organique pour réduire le TACoS.
                </div>
              </Card>

              {/* Recommandations stratégiques (côte-à-côte) — textes améliorés */}
              <Card className="p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-6 text-primary flex items-center gap-2">
                  <Target className="w-6 h-6" />
                  Recommandations stratégiques
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Bloc gauche : Scénario Prix Lancement (60–90 j) */}
                  <div className="border-2 border-primary/30 rounded-lg p-5 bg-card">
                    <div className="font-semibold mb-3 text-primary text-lg">
                      Scénario Prix Lancement — {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(parseFloat(formData.price_launch))}
                    </div>
                    <div className="text-sm text-foreground leading-relaxed">
                      Priorité à la vitesse d'apprentissage et à la visibilité contrôlée. Tester rapidement 10–20 requêtes cœur et 20–40 ASIN de ciblage produits. 
                      Les campagnes SP Auto et Manuel nourrissent la recherche, SB Marque sécurise la demande existante et SD capte les alternatives. 
                      Valider les créas (A+, SB Video) et construire la vitrine Brand Store. 
                      <strong className="block mt-2 text-primary">Objectif :</strong> approcher la rentabilité avec un ACOS sous le break-even, tout en installant la part organique.
                    </div>
                    <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                      <strong>KPI :</strong> ACOS {(economics.acos.launch * 100).toFixed(1)}% · ROAS {economics.roas.launch?.toFixed(2)}× · Budget {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(budgetLaunch)}
                    </div>
                  </div>

                  {/* Bloc droit : Scénario Prix Cible (3–6 mois) */}
                  <div className="border-2 border-primary/30 rounded-lg p-5 bg-card">
                    <div className="font-semibold mb-3 text-primary text-lg">
                      Scénario Prix Cible — {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(parseFloat(formData.price_target))}
                    </div>
                    <div className="text-sm text-foreground leading-relaxed">
                      Une fois les gagnants identifiés, déplacer le budget vers SP Exact et SB Video pour élargir l'audience rentable. 
                      SD remarketing entretient la répétition. 
                      L'hygiène (négatifs, rules, dayparting) et la reallocation mensuelle des budgets permettent de réduire le TACoS. 
                      <strong className="block mt-2 text-primary">Objectif :</strong> ROAS en hausse, marge après pub stable, montée progressive de la part organique.
                    </div>
                    <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                      <strong>KPI :</strong> ACOS {(economics.acos.optimize * 100).toFixed(1)}% · ROAS {economics.roas.optimize?.toFixed(2)}× · Budget {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(budgetOptimize)}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Export PDF Minimal */}
            <div className="max-w-4xl mx-auto">
              <PdfReportAmzAdsMinimal
                brand={formData.brandName}
                product={formData.productName}
                priceLaunch={priceLaunchNum}
                priceTarget={priceTargetNum}
                cogs={cogsNum}
                fba={economics.fba}
                kpiLaunch={{
                  grossMarginPct: (priceLaunchNum - cogsNum) / priceLaunchNum,
                  netNoAdsPct: netNoAdsPctLaunch,
                  breakEvenPct: netNoAdsPctLaunch,
                  acosLaunch: acosLaunchPhase,
                  roasLaunch: acosLaunchPhase > 0 ? 1 / acosLaunchPhase : null,
                  budgetLaunch: budgetLaunch,
                  marginAfterAdsLaunch: netNoAdsPctLaunch - acosLaunchPhase
                }}
                kpiTarget={{
                  grossMarginPct: (priceTargetNum - cogsNum) / priceTargetNum,
                  netNoAdsPct: netNoAdsPctTarget,
                  breakEvenPct: netNoAdsPctTarget,
                  acosOptimize: acosTargetPhase,
                  roasOptimize: acosTargetPhase > 0 ? 1 / acosTargetPhase : null,
                  budgetOptimize: budgetOptimize,
                  marginAfterAdsOptimize: netNoAdsPctTarget - acosTargetPhase
                }}
                waterfallDataLaunch={waterfallDataLaunchPDF.map(d => ({ name: d.label, value: d.value }))}
                waterfallDataTarget={waterfallDataTargetPDF.map(d => ({ name: d.label, value: d.value }))}
                acosRoasData={evolutionData.map(d => ({ 
                  phase: d.mois, 
                  acos: d.acos_cible, 
                  roas: d.roas ?? 0 
                }))}
                advancedParams={advancedParams}
                budgetMin={Math.min(budgetLaunch, budgetGrowth, budgetOptimize)}
                budgetMax={Math.max(budgetLaunch, budgetGrowth, budgetOptimize)}
                budgetPhaseLaunch={budgetLaunch}
                budgetPhaseTarget={budgetOptimize}
              />
            </div>

            {/* Glossaire */}
            <Card className="p-6 max-w-4xl mx-auto bg-muted/30">
              <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Glossaire rapide
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-primary">COGS :</span>{' '}
                  <span className="text-muted-foreground">Coût de revient unitaire du produit</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">Marge brute :</span>{' '}
                  <span className="text-muted-foreground">(Prix − COGS) / Prix (avant frais Amazon)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">Commission Amazon :</span>{' '}
                  <span className="text-muted-foreground">8% si prix ≤ 10€, sinon 15%</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">FBA :</span>{' '}
                  <span className="text-muted-foreground">Frais de logistique Amazon (selon dimensions/poids)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">Marge nette (hors pub) :</span>{' '}
                  <span className="text-muted-foreground">Marge après tous les frais Amazon, avant pub</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">ACOS :</span>{' '}
                  <span className="text-muted-foreground">Advertising Cost of Sales (dépenses pub / ventes pub × 100)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">ACOS break-even :</span>{' '}
                  <span className="text-muted-foreground">Seuil où la pub devient non rentable = Marge nette (hors pub)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">ROAS :</span>{' '}
                  <span className="text-muted-foreground">Return on Ad Spend = 1 / ACOS (ex: ACOS 20% = ROAS 5×)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">SP :</span>{' '}
                  <span className="text-muted-foreground">Sponsored Products (annonces produits)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">SB :</span>{' '}
                  <span className="text-muted-foreground">Sponsored Brands (annonces marques)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">SD :</span>{' '}
                  <span className="text-muted-foreground">Sponsored Display (annonces display)</span>
                </div>
                <div>
                  <span className="font-semibold text-primary">TACoS :</span>{' '}
                  <span className="text-muted-foreground">Total Advertising Cost of Sales (dépenses pub / ventes totales)</span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </TooltipProvider>
    </div>
  );
}
